import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const productsDropdownRef = useRef(null);
  const servicesDropdownRef = useRef(null);

  // Products list - expandable in the future
  const products = [
    { id: 'kidsphere', name: 'Kidsphere', path: '/product', description: 'Revolutionary EdTech Platform' }
    // Add more products here in the future
  ];

  // Services list: new positioning first, then product & advisory offerings
  const services = [
    { id: 'ai', name: 'Intelligent Automation & AI', description: 'LLM workflows & automation', path: '/services/intelligent-automation-ai' },
    { id: 'sec', name: 'Resilient Cybersecurity', description: 'Zero trust & proactive defense', path: '/services/cybersecurity' },
    { id: 'cloud', name: 'Cloud 3.0 Solutions', description: 'AWS, Azure & hybrid', path: '/services/cloud-infrastructure' },
    { id: 'managed', name: 'Managed IT Excellence', description: '24/7 operations & support', path: '/services/managed-it' },
    { id: 'web', name: 'Enterprise Web Development', description: 'Web platforms & APIs', path: '/services/web-development' },
    { id: 'mobile', name: 'Native & Cross-Platform Mobile', description: 'iOS & Android apps', path: '/services/mobile-development' },
    { id: 'edtech', name: 'Revolutionary EdTech Platforms', description: 'Learning technology', path: '/services/edtech-platforms' },
    { id: 'uiux', name: 'Premium UI/UX Design', description: 'Research-led product design', path: '/services/ui-ux-design' },
    { id: 'consulting', name: 'Strategic Technology Consulting', description: 'Strategy, audits & transformation', path: '/services/technology-consulting' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productsDropdownRef.current && !productsDropdownRef.current.contains(event.target)) {
        setIsProductsOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to scroll to section
  const scrollToSection = (sectionId, delay = 0) => {
    const attemptScroll = (attempts = 0) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80; // Navbar height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });
      } else if (attempts < 10) {
        // Retry if element not found (page still loading)
        setTimeout(() => attemptScroll(attempts + 1), 100);
      }
    };
    
    if (delay > 0) {
      setTimeout(() => attemptScroll(), delay);
    } else {
      attemptScroll();
    }
  };

  const navLinks = [
    { id: 'home', label: 'Home', path: '/', sectionId: null, isAnchor: false },
    { id: 'services', label: 'Our Services', path: '/services', sectionId: 'services', isAnchor: true, hasDropdown: true },
    { id: 'portfolio', label: 'Our work', path: '/portfolio', sectionId: null, isAnchor: false },
    { id: 'testimonials', label: 'Testimonials', path: '/#testimonials', sectionId: 'testimonials', isAnchor: true },
    { id: 'about', label: 'About Us', path: '/about', sectionId: 'about', isAnchor: true }
  ];

  const handleNavClick = (e, path, sectionId = null, isAnchor = false) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsProductsOpen(false);
    
    // Handle anchor links or section scrolling
    if (isAnchor && sectionId) {
      if (location.pathname === '/') {
        // On home page, scroll to section
        scrollToSection(sectionId);
      } else {
        // Navigate to home then scroll
        navigate('/');
        scrollToSection(sectionId, 500);
      }
    } else if (path.includes('#')) {
      // Handle paths with hash (e.g., '/#testimonials')
      const hashIndex = path.indexOf('#');
      const sectionIdFromPath = path.substring(hashIndex + 1);
      if (location.pathname === '/') {
        scrollToSection(sectionIdFromPath);
      } else {
        navigate('/');
        scrollToSection(sectionIdFromPath, 500);
      }
    } else if (path === '/') {
      // Home - scroll to top
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Handle regular page navigation
      if (location.pathname !== path) {
        navigate(path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  return (
    <nav className={`navbar-clean ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container-clean">
        <Link 
          to="/" 
          className="navbar-logo-clean" 
          onClick={(e) => {
            e.preventDefault();
            setIsMobileMenuOpen(false);
            handleNavClick(e, '/', null, false);
          }}
        >
          <img src="/sign-no-tagline-transparent-3000x3000.svg" alt="CodeRidge" className="logo-icon-clean" />
          <img src="/logo-transparent.svg" alt="CodeRidge" className="logo-text-clean" />
        </Link>

        <button
          className={`mobile-menu-toggle-clean ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={`navbar-menu-clean ${isMobileMenuOpen ? 'active' : ''}`}>
          <ul className="navbar-links-clean">
            {navLinks.map(link => (
              link.hasDropdown ? (
                <li 
                  key={link.id}
                  className="navbar-dropdown-clean"
                  ref={servicesDropdownRef}
                >
                  <div 
                    style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={() => !window.matchMedia('(max-width: 768px)').matches && setIsServicesOpen(true)}
                    onMouseLeave={() => !window.matchMedia('(max-width: 768px)').matches && setIsServicesOpen(false)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <Link
                        to={link.path}
                        className={`navbar-link-clean ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={(e) => {
                          handleNavClick(e, link.path, link.sectionId, link.isAnchor);
                        }}
                      >
                        {link.label}
                      </Link>
                      <button
                        className="dropdown-arrow-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (window.matchMedia('(max-width: 768px)').matches) {
                            setIsServicesOpen(!isServicesOpen);
                          }
                        }}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.25rem'
                        }}
                        aria-label="Toggle services menu"
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          className={`dropdown-arrow ${isServicesOpen ? 'open' : ''}`}
                        >
                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <ul 
                      className={`services-dropdown ${isServicesOpen ? 'active' : ''}`}
                      onMouseEnter={() => !window.matchMedia('(max-width: 768px)').matches && setIsServicesOpen(true)}
                      onMouseLeave={() => !window.matchMedia('(max-width: 768px)').matches && setIsServicesOpen(false)}
                    >
                      {services.map(service => (
                        <li key={service.id}>
                          <Link
                            to={service.path}
                            className="dropdown-item"
                            onClick={(e) => {
                              handleNavClick(e, service.path, false);
                              setIsServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                          >
                            <div className="dropdown-item-content">
                              <span className="dropdown-item-name">{service.name}</span>
                              <span className="dropdown-item-desc">{service.description}</span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              ) : (
                <li key={link.id}>
                  <Link
                    to={link.path}
                    className={`navbar-link-clean ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, link.path, link.sectionId, link.isAnchor)}
                  >
                    {link.label}
                  </Link>
                </li>
              )
            ))}
            <li 
              className="navbar-dropdown-clean"
              ref={productsDropdownRef}
            >
              <div 
                style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                onMouseEnter={() => !window.matchMedia('(max-width: 768px)').matches && setIsProductsOpen(true)}
                onMouseLeave={() => !window.matchMedia('(max-width: 768px)').matches && setIsProductsOpen(false)}
              >
                <span 
                  className={`navbar-link-clean ${location.pathname === '/product' ? 'active' : ''}`}
                  onClick={() => {
                    if (window.matchMedia('(max-width: 768px)').matches) {
                      setIsProductsOpen(!isProductsOpen);
                    } else {
                      navigate('/product');
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Products
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={`dropdown-arrow ${isProductsOpen ? 'open' : ''}`}>
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <ul 
                  className={`products-dropdown ${isProductsOpen ? 'active' : ''}`}
                  onMouseEnter={() => !window.matchMedia('(max-width: 768px)').matches && setIsProductsOpen(true)}
                  onMouseLeave={() => !window.matchMedia('(max-width: 768px)').matches && setIsProductsOpen(false)}
                >
                  {products.map(product => (
                    <li key={product.id}>
                      <Link
                        to={product.path}
                        className="dropdown-item"
                        onClick={(e) => {
                          handleNavClick(e, product.path);
                          setIsProductsOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-name">{product.name}</span>
                          <span className="dropdown-item-desc">{product.description}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          </ul>
          <Link
            to="/contact"
            className="navbar-cta-clean"
            onClick={(e) => {
              setIsMobileMenuOpen(false);
              handleNavClick(e, '/contact', false);
            }}
          >
            Get in touch
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
